import { useTranslation, Trans } from 'react-i18next'
import {
  Text,
  Box,
  Columns,
  Column,
  Stack,
  List,
  TextLinkButton,
  useDialogController,
  Skeleton,
  Inline
} from '@machobear/design-system'
import { useLocation, useHistory } from 'react-router'
import useMediaQuery from '@/hooks/use-media-query'
import capitalize from '@/utils/capitalize'
import StatusBadge from '@/components/status-badge'
import formatCurrency from '@/utils/format-currency'
import StandaloneRiskBar from './standalone-risk-bar'
import BreakdownDialog from './breakdown-dialog'

const RentToIncome = ({ data, isLocked, standalone, application, isLoading }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const isMobile = useMediaQuery('(max-width: 767px)')

  const {
    visible: isBreakdownDialogVisible,
    showModal: showBreakdownDialog,
    closeModal: closeBreakdownDialog
  } = useDialogController(false)

  const combinedPercentageUsage = data?.joint_income?.percentage_usage
  const scoreText = Math.round(combinedPercentageUsage)

  const getScoreText = score => {
    if (score >= 50) {
      return '#DC2C2D'
    } else if (score >= 30) {
      return '#DFA414'
    } else {
      return '#12784F'
    }
  }

  const getRiskProfileText = score => {
    if (score >= 50) {
      return (
        <Text size="small" tone="secondary">
          <Trans i18nKey="l.standalone.view.standalone_rent_to_income.risk_profile.high">
            A high rent-to-income ratio signals that this tenant might be a{' '}
            <span style={{ color: '#DC2C2D' }}>financial risk</span>. While they may have other
            funds not represented here, spending over 50% of their income on rent suggests potential
            difficulties covering living expenses and unexpected costs.
          </Trans>
        </Text>
      )
    } else if (score >= 30) {
      return (
        <Text size="small" tone="secondary">
          <Trans i18nKey="l.standalone.view.standalone_rent_to_income.risk_profile.medium">
            A medium rent-to-income ratio indicates that this tenant is{' '}
            <span style={{ color: '#723D02' }}>mostly reliable</span> financially as they have
            enough income to cover rent, but may have difficulty making payments if unexpected
            expenses arise.
          </Trans>
        </Text>
      )
    } else {
      return (
        <Text size="small" tone="secondary">
          <Trans i18nKey="l.standalone.view.standalone_rent_to_income.risk_profile.low">
            A low rent-to-income ratio indicates that this tenant is{' '}
            <span style={{ color: '#12784F' }}>financially reliable</span> as they can comfortably
            afford rent and have extra income to cover unexpected expenses.
          </Trans>
        </Text>
      )
    }
  }

  const hiddenPlaceholder = '***** *****'
  const hiddenPlaceholderLong = '***** ******* ***** ****** *****'

  const pathToDocuments = location?.pathname?.replace('trust-score', 'documents')
  const hasRoomates = data?.income_breakdown?.length > 1
  const hasPrepaid = data?.income_breakdown?.some(item => item.is_prepaid)
  const monthlyIncome = hasPrepaid
    ? 'Prepay Rent'
    : `${formatCurrency(data?.joint_income?.monthly_income, { hideCents: true })} / month`

  const navigateToDocuments = () => {
    history.push(pathToDocuments)
  }

  const handleViewButton = () => {
    if (hasRoomates) {
      showBreakdownDialog()
    } else {
      navigateToDocuments()
    }
  }

  return (
    <>
      <Stack space="small">
        <Box display="flex">
          <Box display="flex" justifyContent="spaceBetween" alignItems="center" width="full">
            <Trans i18nKey={'l.application.view.standalone_rent_to_income.label'} t={t}>
              <Text weight="medium">Insights</Text>
            </Trans>
          </Box>

          <StatusBadge
            title={capitalize(data?.rate)}
            type={data?.rate}
            size="small"
            isLocked={isLocked}
          />
        </Box>

        <Columns space="small" collapseBelow="wide" reverse>
          <Column width={{ mobile: 'full', desktop: 'content' }}>
            <Box
              border="borderNeutralLight"
              overflow="hidden"
              borderRadius="small"
              display="flex"
              flexDirection="column"
            >
              <Box
                overflow="hidden"
                padding="gutter"
                paddingBottom="none"
                display="flex"
                flexDirection={{ mobile: 'column', tablet: 'row' }}
                gap={{ mobile: 'small', tablet: 'large' }}
                style={
                  isLocked
                    ? { filter: 'blur(10px)', msUserSelect: 'none', userSelect: 'none' }
                    : null
                }
              >
                <Box paddingTop="xxsmall">
                  {isLoading ? (
                    <Skeleton.Shape height={55} width={57} />
                  ) : (
                    <Box
                      display="flex"
                      gap="xsmall"
                      justifyContent="center"
                      flexDirection={{ mobile: 'row', tablet: 'column' }}
                    >
                      <Box
                        weight="strong"
                        background={isLocked ? 'disabled' : 'white'}
                        cursor={isLocked ? 'not-allowed' : 'default'}
                        style={{
                          fontSize: isMobile ? '24px' : '32px',
                          fontWeight: 700,
                          color: isLocked ? 'black' : getScoreText(scoreText)
                        }}
                      >
                        {isLocked
                          ? hiddenPlaceholder.slice(0, 2)
                          : Math.round(combinedPercentageUsage)}
                        %
                      </Box>
                      <Box
                        style={{
                          fontSize: isMobile ? '20px' : '16px',
                          fontWeight: 700,
                          color: isLocked ? 'black' : getScoreText(scoreText),
                          whiteSpace: 'nowrap'
                        }}
                        background={isLocked ? 'disabled' : 'white'}
                        cursor={isLocked ? 'not-allowed' : 'default'}
                      >
                        {isLocked
                          ? hiddenPlaceholder.slice(0, 5)
                          : scoreText < 50
                          ? scoreText < 30
                            ? 'Low Risk'
                            : 'Medium Risk'
                          : 'High Risk'}
                      </Box>
                    </Box>
                  )}
                </Box>

                {isLoading ? (
                  <Skeleton.Shape height={55} width={'100%'} />
                ) : (
                  <StandaloneRiskBar score={scoreText} rate="excellent" isLocked={isLocked} />
                )}
              </Box>
              <Box
                marginTop="large"
                display="flex"
                flexDirection="column"
                gap="xxsmall"
                padding="gutter"
                paddingTop="none"
              >
                <Text size="small" weight="strong" baseline={false} tone="secondary">
                  Risk Profile:
                </Text>
                <Box
                  background={isLocked ? 'disabled' : 'white'}
                  cursor={isLocked ? 'not-allowed' : 'default'}
                  display="flex"
                  flexDirection="column"
                  gap="xsmall"
                  style={
                    isLocked
                      ? { filter: 'blur(10px)', msUserSelect: 'none', userSelect: 'none' }
                      : null
                  }
                >
                  {isLoading ? (
                    <>
                      <Skeleton.Shape height={21} width={'50%'} />
                      <Skeleton.Shape height={45} width={'80%'} />
                    </>
                  ) : (
                    <List space="xxsmall">
                      {isLocked ? (
                        hiddenPlaceholderLong
                      ) : (
                        <Text size="small" baseline={false} tone="secondary">
                          {Math.round(combinedPercentageUsage)}% of their total monthly income is
                          spent on rent.
                        </Text>
                      )}
                      {isLocked
                        ? hiddenPlaceholderLong + hiddenPlaceholderLong
                        : getRiskProfileText(scoreText)}
                    </List>
                  )}
                </Box>
              </Box>
            </Box>
          </Column>

          <Column width="content">
            <Box display="flex" flexDirection="column" gap="small">
              <Box border="borderNeutralLight" borderRadius="small" width="full">
                <Box
                  padding="xsmall"
                  background="neutralLight"
                  borderBottom="borderNeutralLight"
                  style={{ borderRadius: '4px 4px 0 0' }}
                >
                  <Text size="small" weight="strong" tone="neutral">
                    {t('l.standalone.view.rental_price.label', 'Rental price')}
                  </Text>
                </Box>

                <Box
                  padding="xsmall"
                  borderRadius="small"
                  background={isLocked ? 'disabled' : 'white'}
                  cursor={isLocked ? 'not-allowed' : 'default'}
                  style={{
                    borderRadius: '0 0 4px 4px',
                    ...(isLocked
                      ? { filter: 'blur(10px)', msUserSelect: 'none', userSelect: 'none' }
                      : {})
                  }}
                >
                  {isLoading ? (
                    <Skeleton.Shape height={11} width={'100%'} />
                  ) : (
                    <Text size="small" tone="secondary">
                      {isLocked
                        ? hiddenPlaceholder
                        : formatCurrency(data?.rental_price, { hideCents: true })}{' '}
                      / month
                    </Text>
                  )}
                </Box>
              </Box>

              <Box border="borderNeutralLight" borderRadius="small" width="full">
                <Box
                  padding="xsmall"
                  background="neutralLight"
                  borderBottom="borderNeutralLight"
                  style={{ borderRadius: '4px 4px 0 0' }}
                >
                  <Text size="small" weight="strong" tone="neutral">
                    {t('l.standalone.view.total_monthly_income.label', 'Total monthly income')}
                  </Text>
                </Box>

                <Box
                  padding="xsmall"
                  display="flex"
                  flexDirection="column"
                  gap="small"
                  borderRadius="small"
                  background={isLocked ? 'disabled' : 'white'}
                  cursor={isLocked ? 'not-allowed' : 'default'}
                  style={{
                    borderRadius: '0 0 4px 4px',
                    ...(isLocked
                      ? { filter: 'blur(10px)', msUserSelect: 'none', userSelect: 'none' }
                      : {})
                  }}
                >
                  {isLoading ? (
                    <Skeleton.Shape height={45} width={'100%'} />
                  ) : (
                    <>
                      <Text size="small" tone="secondary">
                        {isLocked ? hiddenPlaceholder : monthlyIncome}
                      </Text>
                      {!standalone && !isLocked && (
                        <TextLinkButton onClick={handleViewButton} weight="regular">
                          {hasRoomates ? 'View breakdown' : 'View documents'}
                        </TextLinkButton>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Column>
        </Columns>
      </Stack>
      <BreakdownDialog
        visible={isBreakdownDialogVisible}
        closeModal={closeBreakdownDialog}
        data={data?.income_breakdown ?? []}
        navigateToDocuments={navigateToDocuments}
        application={application}
      />
    </>
  )
}

export default RentToIncome
